import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { api } from '@/utils/api';
import { ponteDoAplicativo, aplicarNaSalaSemInternet, type AparelhoDaSala, type SalaPreparada } from './aplicativo';
import { consultarChaveDaSala, controle, estadoDoMotor, prepararNoCelular, salaNoCelularDisponivel } from '@/features/salaNoCelular/salaNoCelular';
import { confirmarDono, contaDaSalaNoCelular } from './contaDaSala';

/**
 * A SALA SEM INTERNET NESTE APARELHO, num lugar só (02/11/2026; aplicativo do computador 2.0.20 e app do celular 1.6). O
 * computador responde pela ponte (`window.portalSimulados`); o celular e o iPad, pelo motor da sala (`controle`). O cartão
 * do simulado, a Configuração e o preparo automático (`PreparoAutomatico.tsx`) perguntam AQUI — não à ponte ou ao motor
 * direto. O que o aparelho não sabe fazer (o 2.0.19, o app 1.5) volta nulo ou falso, e a tela esconde.
 */
export async function salasDoAparelho(): Promise<SalaPreparada[]> {
  const ponte = ponteDoAplicativo();
  if (ponte) return ponte.salasPreparadas();
  void consultarChaveDaSala(); // a chave do iPad/tablet, no máximo a cada 30 min
  if (salaNoCelularDisponivel()) return (await controle<{ salas: SalaPreparada[] }>('GET', '/estado')).salas;
  return [];
}

/** Este aparelho (o id e a marca "aplica salas sem internet"), ou nulo quando ele não sabe (versão anterior, navegador). */
export async function aparelhoDaSala(): Promise<AparelhoDaSala | null> {
  const ponte = ponteDoAplicativo();
  if (ponte) return ponte.aparelho ? ponte.aparelho().catch(() => null) : null;
  if (!salaNoCelularDisponivel()) return null;
  const est = await estadoDoMotor().catch(() => null);
  return est?.aparelho ?? null;
}

/** A chave de Configuração: liga ou desliga "este aparelho aplica salas sem internet". */
export async function definirAplicaSalas(valor: boolean): Promise<AparelhoDaSala | null> {
  const ponte = ponteDoAplicativo();
  if (ponte) return ponte.definirAplicaSalas ? ponte.definirAplicaSalas(valor) : null;
  const r = await controle<{ aparelho: AparelhoDaSala }>('POST', '/aparelho', { aplicaSalas: valor });
  return r.aparelho;
}

/** Prepara sozinho (o aparelho marcado; `PreparoAutomatico`). */
export async function prepararSozinho(simulationId: number): Promise<void> {
  if (ponteDoAplicativo()) { await aplicarNaSalaSemInternet(simulationId, undefined, { automatica: true }); return; }
  await prepararNoCelular(simulationId, undefined, { automatica: true });
}

/** O aparelho sabe apagar a sala e salvar o arquivo? (2.0.20 / motor 1.6) */
export async function sabeApagar(): Promise<boolean> {
  const ponte = ponteDoAplicativo();
  if (ponte) return typeof ponte.apagarSala === 'function';
  if (!salaNoCelularDisponivel()) return false;
  return !!(await estadoDoMotor().catch(() => null))?.sabe?.includes('apagar');
}

/**
 * O motor deste celular sabe da CONTA DA SALA? (1.6 com o nativo de 02/11/2026: o motor recusa a conta errada e o cofre faz
 * o desbloqueio do aparelho). O app de antes (1.5, ou a 1.6 compilada antes disto) não sabe: aí a tela abre e apaga como
 * antes, sem pedir desbloqueio — pedir o que o binário não tem travaria a sala.
 */
export async function sabeConta(): Promise<boolean> {
  if (ponteDoAplicativo() || !salaNoCelularDisponivel()) return false;
  return !!(await estadoDoMotor().catch(() => null))?.sabe?.includes('conta');
}

/**
 * "Apagar deste aparelho". Com resultado a enviar, a primeira chamada volta `{ pendente: true, erro }`; a tela confirma e
 * chama de novo com `mesmoComPendentes`.
 */
export async function apagarDoAparelho(id: number, mesmoComPendentes = false): Promise<{ ok: true } | { ok: false; pendente: boolean; erro: string }> {
  const ponte = ponteDoAplicativo();
  if (ponte?.apagarSala) {
    const r = await ponte.apagarSala(id, { mesmoComPendentes });
    return r.ok ? { ok: true } : { ok: false, pendente: r.codigo === 'TEM_PENDENTES', erro: r.erro ?? 'Não foi possível apagar.' };
  }
  // No celular (02/11/2026): a conta da sala e o desbloqueio do aparelho antes de apagar — só no motor que sabe (`sabeConta`).
  const comConta = await sabeConta();
  if (comConta) {
    try { await confirmarDono('Apagar a sala deste aparelho'); } catch (e) { return { ok: false, pendente: false, erro: (e as { cancelado?: boolean }).cancelado ? 'Cancelado.' : (e as Error).message }; }
  }
  try { await controle('POST', '/apagar', { id, mesmoComPendentes, ...(comConta ? { conta: await contaDaSalaNoCelular() } : {}) }); return { ok: true }; }
  catch (e) { const erro = (e as Error).message; return { ok: false, pendente: /portal ainda não recebeu/.test(erro), erro }; }
}

/** "Salvar arquivo": o que foi colhido. Computador: onde o professor escolher. Celular: grava e abre o compartilhar. */
export async function salvarArquivoDaSala(id: number): Promise<string | null> {
  const ponte = ponteDoAplicativo();
  if (ponte?.salvarArquivoDaSala) {
    const r = await ponte.salvarArquivoDaSala(id);
    if (!r.ok) throw new Error(r.erro ?? 'Não foi possível salvar o arquivo.');
    return r.salvo ? r.caminho ?? null : null;
  }
  const r = await controle<{ nome: string; arquivo: unknown }>('POST', '/arquivo', { id, ...(await sabeConta() ? { conta: await contaDaSalaNoCelular() } : {}) });
  const salvo = await Filesystem.writeFile({ path: r.nome, data: JSON.stringify(r.arquivo, null, 2), directory: Directory.Documents, encoding: Encoding.UTF8 });
  if (Capacitor.isNativePlatform()) await Share.share({ title: 'Resultados da sala sem internet', url: salvo.uri }).catch(() => { /* o professor fechou o compartilhar: o arquivo ficou em Documentos */ });
  return r.nome;
}

/** A entrega da sala de versão anterior: "Conferi" (02/11/2026; o servidor dá a nota). */
export async function conferirVersaoAnterior(responseId: number): Promise<{ nota: number | null }> {
  return (await api.post(`/api/offline-rooms/entregas/${responseId}/conferir-versao`, {})).data;
}
